import { isNumber } from 'lodash'
import { virtualGridProps } from '../../props'
import { useGridWheel } from './use-grid-wheel'
import { createColVnode } from '../header/utils'

import { createScrollBar } from '../scroll-bar'
import { initRowStyleCache, getRowClassName, getRTLOffsetType } from './utils'
import {
  RTL,
  RTL_OFFSET_NAG,
  RTL_OFFSET_POS_ASC,
  TABLE_EVENTS,
  BORDER_VALUE,
  MAIN_TABLE,
  VERTICAL_SCROLL_BAR_LAYOUT,
  HORIZONTAL_SCROLL_BAR_LAYOUT
} from '../../config'
import '../../style/virtual-grid.scss'

const getRowStyle = initRowStyleCache()
const VerticalScrollbar = createScrollBar()
const HorizontalScrollbar = createScrollBar()

export function createVirtualGrid() {
  return {
    name: 'VirtualGrid',
    props: virtualGridProps,
    data() {
      const { scrollPreventDefault, stopWheel } = this.store.table.$props
      if (stopWheel) {
        this.onWheel = () => {}
      } else {
        let _preDirection = 'X'
      const { onWheel } = useGridWheel((x, y) => {
        const scrollLeft = this.scroll.scrollLeft + x
        const scrollTop = this.scroll.scrollTop + y
        if (x) {
          _preDirection = 'X'
        }
        if (y) {
          _preDirection = 'Y'
        }
        this.scrollTo({ scrollLeft, scrollTop, type: 'wheel' }, _preDirection)
        }, this.$props.rowHeight, scrollPreventDefault)
        this.onWheel = onWheel
      }
      return {
        scroll: {
          scrollLeft: this.store.states.scrollPos.scrollLeft || 0,
          scrollTop: this.store.states.scrollPos.scrollTop || 0,
        }
      }
    },
    computed: {
      isMainTable () {
        return this.$props.type === MAIN_TABLE
      },
      innerStyle() {
        const { columnTotalWidth, rowTotalHeight } = this.$props
        const estimatedTotalWidth = columnTotalWidth
        const estimatedTotalHeight = rowTotalHeight
        const width = `${estimatedTotalWidth}px`
        const height = `${estimatedTotalHeight}px`
        return { height, width }
      },
      windowStyle() {
        let { height, width, style } = this.$props
        if (this.isMainTable) {
          width -= BORDER_VALUE
        }
        const tableStyle = {
          direction: this.$props.direction,
          width: isNumber(width) ? `${width}px` : width,
        }
        if (height) {
          tableStyle.height = isNumber(height) ? `${height}px` : height
        }
        return [
          {
            position: 'relative',
            overflow: 'hidden',
            WebkitOverflowScrolling: 'touch',
            willChange: 'transform',
          },
          tableStyle,
          style || {},
        ]
      },
      $vScrollbar() {
        return this.$refs.vScrollbar
      },
      $hScrollbar() {
        return this.$refs.hScrollbar
      },
      $window() {
        return this.$refs.windowRef
      },
    },
    created () {
      // listen item size change
      this.$props.enableDynamicHeight && this.$on('row_resize', this.onRowResized)
    },
    beforeDestroy () {
      this.$props.enableDynamicHeight && this.$off('row_resize', this.onRowResized)
    },
    methods: {
      onRowResized (id, size, rowIndex) {
        this.store.setVerticalVirtualSizeMap(id, size, rowIndex)
      },
      onUpdated({
        scrollLeft,
        scrollTop
      }) {
        const { direction } = this.$props
        const windowElement = this.$window
        if (!windowElement) return
        windowElement.scrollTop = Math.max(0, scrollTop)
        if (!this.isMainTable) return
        // 暂时用不到此处逻辑
        if (direction === RTL) {
          switch (getRTLOffsetType()) {
            case RTL_OFFSET_NAG: {
              windowElement.scrollLeft = -scrollLeft
              break
            }
            case RTL_OFFSET_POS_ASC: {
              windowElement.scrollLeft = scrollLeft
              break
            }
            default: {
              const { clientWidth, scrollWidth } = windowElement
              windowElement.scrollLeft = scrollWidth - clientWidth - scrollLeft
              break
            }
          }
        } else {
          windowElement.scrollLeft = Math.max(0, scrollLeft)
        }
      },
      doScroll({ scrollLeft = this.scroll.scrollLeft, scrollTop = this.scroll.scrollTop, type = 'wheel' } = {}) {
        this.scroll.scrollLeft = scrollLeft
        this.scroll.scrollTop = scrollTop
        this.store.virtualInstance.handleScroll(this.scroll.scrollTop)
        // 是否开启 水平虚拟滚动
        this.store.horizontalVirtual && this.store.horizontalVirtual.handleScroll(this.scroll.scrollLeft)
        this.updateScrollBar(this.scroll)
        if (type === 'wheel') {
          this.onUpdated(this.scroll)
        } else {
          this.$nextTick(() => {
            this.onUpdated(this.scroll)
          })
        }
      },
      updateScrollBar({ scrollTop, scrollLeft }) {
        const props = this.$props
        this.$vScrollbar && this.$vScrollbar.updateTraveled(Math.max(0, scrollTop / (props.rowTotalHeight - props.height)))
        this.$hScrollbar && this.$hScrollbar.updateTraveled(Math.max(0, scrollLeft / (props.columnTotalWidth - props.width)))
      },
      scrollTo({ scrollLeft = this.scroll.scrollLeft, scrollTop = this.scroll.scrollTop, type = 'wheel' } = {}, direction) {
        // 用户不传入高
        if (this.store.tableBodyHeight <= 0) return
        const { rowTotalHeight = 0, columnTotalWidth = 0, height = 0 } = this.$props
        // 边界判断
        scrollLeft = Math.max(Math.min(scrollLeft, columnTotalWidth - this.tableWidth), 0)
        scrollTop = Math.max(Math.min(scrollTop, rowTotalHeight - height), 0)
        this.onClearUserAction()
        this.emitEvents({ scrollLeft, scrollTop, type, direction })
      },
      emitEvents(params) {
        this.$emit(TABLE_EVENTS.scroll, params)
      },
      onVerticalScroll(distance, totalSteps) {
        const props = this.$props
        const height = this.height
        const offset = ((props.rowTotalHeight - height) / totalSteps) * distance
        const scrollTop = Math.min(props.rowTotalHeight - height, offset)
        this.scrollTo({ scrollTop: Math.min(props.rowTotalHeight - height, scrollTop), type: 'scrollBar' }, 'Y')
      },
      onHorizontalScroll(distance, totalSteps) {
        const props = this.$props
        const width = this.tableWidth
        const offset = ((props.columnTotalWidth - width) / totalSteps) * distance
        const scrollLeft = Math.min(props.columnTotalWidth - width, offset)
        this.scrollTo({ scrollLeft: Math.min(props.columnTotalWidth - width, scrollLeft), type: 'scrollBar' }, 'X')
      },
      onScroll(e) {
        const { scrollTop, scrollLeft } = e.currentTarget
        const { scrollTop: _scrollTop, scrollLeft: _scrollLeft } = this.scroll
        if (
          Math.floor(_scrollTop) === Math.floor(scrollTop) &&
          Math.floor(_scrollLeft) === Math.floor(scrollLeft)
        ) {
          return
        }
        // 为了兼容固定左边/右边也能滑动
        if (
          Math.floor(_scrollTop) === Math.floor(scrollTop) &&
          Math.floor(_scrollLeft) !== Math.floor(scrollLeft) &&
          !this.isMainTable
        ) return
        this.scrollTo({ scrollTop: _scrollTop, scrollLeft: _scrollLeft })
      },
      onClearUserAction() {
        // 用户双击文本选中，滚动清空
        const selection = window.getSelection()
        let selectionElClassName = selection && selection.focusNode
        selectionElClassName = selectionElClassName && selectionElClassName.className
        if (selectionElClassName && selectionElClassName.includes('virtual-grid__inner__table__row__cell__label')) {
          selection.removeAllRanges()
        }
      }
    },
    render(h) {
      const props = this.$props
      const store = this.store
      const { renderData, selectdInfo } = store.states
      const isRenderEmptyNode = !renderData.length
      const renderRowItems = () => {
        const [slots] = this.$slots.default || []
        const { start, end } = store.states.verticalVirtualRange
        const children = []
        let i = 0
        for (let rowIndex = start; rowIndex <= end; rowIndex++) {
          const curRenderIndex = i
          const rowData = renderData[i++]
          children.push(
            slots.default({
              rowIndex,
              rowData,
              renderDataIndex: curRenderIndex,
              rowClassName: getRowClassName({
                highlight: rowIndex === selectdInfo.currentRowIndex && props.highlightCurrentRow
              }),
              rowStyle: getRowStyle({
                // highlight: rowIndex === selectdInfo.currentRowIndex && props.highlightCurrentRow,
                rowIndex,
                rowHeight: props.rowHeight,
                columnTotalWidth: props.columnTotalWidth
              }),
              rowHeight: props.rowHeight,
              offsetY: props.rowHeight * rowIndex,
              scrollTop: this.scroll.scrollTop,
              isMainTable: this.isMainTable
            })
          )
        }
        return children
      }

      const renderInner = () => {
        const children = renderRowItems()
        const Inner = 'div'
        const { horizontalVirtualRange } = this.store.states
        const { padFront: horizontalPadFront } = horizontalVirtualRange
        let columns = this.columns
        if (this.isMainTable) {
          columns = this.store.viewportRenderColumns
        }
        const colVnode = createColVnode(h, columns)
        const { padFront } = store.states.verticalVirtualRange
        const isMainTable = this.isMainTable
        const paddingStyle = {
          position: 'absolute',
          transform: `translateY(${padFront}px) translateX(${isMainTable ? horizontalPadFront : 0}px)`,
        }
        const TableContent = h('table',
          {
            class: 'virtual-grid__inner__table',
          },
          [
            <colgroup>
              { colVnode }
            </colgroup>,
            <tbody>
              { children }
            </tbody>
        ])

        return [
          h(Inner,
            {
              class: 'virtual-grid__inner',
              style: this.innerStyle,
            },
            [
              <div style={paddingStyle}>
                { TableContent }
              </div>
            ]
          )
        ]
      }
      const renderEmpty = () => {
        const [slots] = this.$slots.default || []
        return [slots.emptyNode || null]
      }
      const Inner = [isRenderEmptyNode ? renderEmpty() : renderInner()]
      return h(
        'div',
        {
          class: 'kye-table-v2__virtual-grid',
        },
        [
          h(
            this.containerElement,
            {
              class: this.className,
              style: this.windowStyle,
              on: {
                wheel: this.onWheel,
                scroll: this.onScroll
              },
              ref: 'windowRef',
            },
            typeof this.containerElement !== 'string' ? { default: () => Inner } : Inner
          ),
          h(HorizontalScrollbar, {
            ref: 'hScrollbar',
            props: {
              alwaysOn: props.scrollbarAlwaysOn,
              startGap: 0,
              endGap: 2,
              className: 'virtual-srcollbar-horizontal',
              clientSize: this.width,
              layout: HORIZONTAL_SCROLL_BAR_LAYOUT,
              ratio: Math.floor((this.width * 100) / this.columnTotalWidth),
              total: store.states.tableDataLength,
              offset: store.states.scrollOffsetX,
              visible: true,
              scrollbarSize: props.hScrollbarSize,
              scrollbarPosition: store.states.scrollbarPosition,
              scrollbarStyle: props.scrollbarStyle
            },
            on: {
              scroll: this.onHorizontalScroll
            }
          }),
          h(VerticalScrollbar, {
            ref: 'vScrollbar',
            props: {
              alwaysOn: props.scrollbarAlwaysOn,
              startGap: 0,
              endGap: 2,
              className: 'virtual-srcollbar-vertical',
              clientSize: this.height,
              layout: VERTICAL_SCROLL_BAR_LAYOUT,
              ratio: Math.floor((this.height * 100) / this.rowTotalHeight),
              total: store.states.tableDataLength,
              visible: true,
              scrollbarSize: props.vScrollbarSize,
              scrollbarStyle: props.scrollbarStyle
            },
            on: {
              scroll: this.onVerticalScroll
            }
          })
        ]
      )
    },
  }
}



// WEBPACK FOOTER //
// ./src/public/components/kye-table2/components/virtual-grid/index.js